import { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { authenticatedFetch } from '../utils/auth';

/**
 * Hook to manage team permissions
 * @returns {Object} Team permissions utilities
 */
export const useTeamPermissions = () => {
  const { keycloak } = useKeycloak();
  const [isLoading, setIsLoading] = useState(false);
  const [teamAccessMap, setTeamAccessMap] = useState({});
  const [error, setError] = useState(null);

  /**
   * Fetch team permissions for the current user
   */
  const fetchTeamPermissions = useCallback(async () => {
    if (!keycloak.authenticated) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authenticatedFetch(
        'api/teams/my-permissions',
        { method: 'GET' },
        keycloak.token
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch team permissions');
      }
      
      const data = await response.json();
      
      // Create a map of team_id -> access_level for quick lookups
      const accessMap = {};
      data.forEach(permission => {
        accessMap[permission.team_id] = permission.access_level;
      });
      
      setTeamAccessMap(accessMap);
    } catch (err) {
      // Quietly handle auth check failures
    } finally {
      setIsLoading(false);
    }
  }, [keycloak.authenticated, keycloak.token]);

  useEffect(() => {
    fetchTeamPermissions();
  }, [fetchTeamPermissions]);

  /**
   * Check if user owns the dashboard
   * @param {Object} dashboard The dashboard object
   * @returns {boolean} True if user is the owner
   */
  const isOwner = useCallback((dashboard) => {
    // Simple direct comparison of owner_email to current user's email
    const isCurrentUserOwner = dashboard?.owner_email === keycloak.tokenParsed?.email;
    return isCurrentUserOwner;
  }, [keycloak.tokenParsed?.email]);

  /**
   * Check if user has write access to the dashboard
   * @param {Object} dashboard The dashboard object
   * @returns {boolean} True if user has write access
   */
  const hasWriteAccess = useCallback((dashboard) => {
    // Owner always has write access
    if (isOwner(dashboard)) {
      return true;
    }
    
    // Check if the user has any write access from the API permissions
    const hasAnyWriteAccess = Object.values(teamAccessMap).some(access => access === 'write');
    
    if (hasAnyWriteAccess) {
      return true;
    }
    
    return false;
  }, [isOwner, teamAccessMap]);

  /**
   * Check if user has read access to the dashboard
   * @param {Object} dashboard The dashboard object
   * @returns {boolean} True if user has at least read access
   */
  const hasReadAccess = useCallback((dashboard) => {
    // Owner always has read access
    if (isOwner(dashboard)) return true;
    
    // If dashboard is not shared or has no team_id, only owner has access
    if (!dashboard.team_id) return false;
    
    // Any access level grants read access
    return !!teamAccessMap[dashboard.team_id];
  }, [isOwner, teamAccessMap]);

  return {
    isLoading,
    error,
    isOwner,
    hasWriteAccess,
    hasReadAccess,
    refreshPermissions: fetchTeamPermissions
  };
}; 